import {
  booleanAttribute,
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  HostListener,
  inject,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PillSearchComponent, SearchState } from '../pill-search/pill-search.component';
import { useFallbackIcon } from '@olv/ui-shared/utils/asset-balance.utils';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatProgressSpinnerModule, PillSearchComponent],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnChanges {
  protected readonly useFallbackIcon = useFallbackIcon;
  @Input() id = '';
  @Input() label: string | undefined;
  @Input() ariaLabel: string | undefined;
  @Input() placeholder = 'Select an option';
  @Input() options: { value: any; label: string; disabled?: boolean }[] = [];
  @Input() isInvalid = false;
  @Input() errorMessage: string | undefined;
  @Input() disabled = false;
  @Input() iconPath: string | undefined; // Changed from iconName to iconPath
  @Input() type: 'default' | 'checkbox' = 'default';
  @Input({ transform: booleanAttribute }) clearable = false;
  @Input() searchable = false;
  @Input() isSearch = false;
  @Input() isFilterClass = false;
  @Input() disableScroll = false;
  @Input() loading = false;
  @Input() allowAdd = false;
  @Input() addButtonLabel = 'Add Item';
  @Input() searchPlaceholder = 'Searching...';

  @Output() addItem = new EventEmitter<string>();

  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  @Input() value: any = null;
  isOpen = false;
  hasOpenedDropdown = false;
  selectedLabel = '';
  searchTerm = '';

  // Control Value Accessor methods
  onChange: any = () => {};
  onTouched: any = () => {};

  searchText = '';
  filteredOptions: { value: any; label: string; disabled?: boolean }[] = [];

  private elementRef = inject(ElementRef);

  ngOnInit() {
    this.filteredOptions = [...this.options];
  }

  ngOnChanges(): void {
    this.filteredOptions = [...this.options];
    this.updateSelectedLabel();
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedLabel();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateSelectedLabel(): void {
    const selectedOption = this.options.find((option) => option.value === this.value);
    this.selectedLabel = selectedOption ? selectedOption.label : '';
  }

  hasValue(): boolean {
    if (this.type === 'checkbox') {
      return Array.isArray(this.value) && this.value.length > 0;
    }
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get showClear(): boolean {
    return this.clearable && !this.disabled && !this.loading && this.hasValue();
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();

    const cleared = this.type === 'checkbox' ? [] : null;

    this.value = cleared;
    this.updateSelectedLabel();
    this.onChange(cleared);
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    const openedDropdown = this.isOpen;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.onTouched();
    } else {
      this.searchTerm = '';
    }

    if (openedDropdown && !this.isOpen) {
      this.hasOpenedDropdown = true;
    }
  }
  isSelected(option: { value: any; label: string }): boolean {
    if (this.type === 'checkbox') {
      return Array.isArray(this.value) && this.value.includes(option.value);
    }
    return this.value === option.value;
  }
  selectOption(option: { value: any; label: string }): void {
    this.value = option.value;
    this.selectedLabel = option.label;
    this.isOpen = false;
    this.hasOpenedDropdown = true;
    this.onChange(this.value);
  }

  toggleCheckboxOption(option: { value: any; label: string }) {
    let newValue: any[];

    if (Array.isArray(this.value)) {
      const exists = this.value.includes(option.value);

      newValue = exists ? this.value.filter((v) => v !== option.value) : [...this.value, option.value];
    } else {
      newValue = [option.value];
    }

    this.value = newValue;
    this.updateSelectedLabel();
    this.onChange(newValue);
  }
  onOptionClicked(option: { value: any; label: string; disabled?: boolean }, event: Event): void {
    event.stopPropagation();
    if (option.disabled) return;
    if (this.type === 'checkbox') {
      this.toggleCheckboxOption(option);
    } else {
      this.selectOption(option);
    }
  }
  getSelectedLabels(): string[] {
    if (this.type === 'checkbox' && Array.isArray(this.value)) {
      return this.options.filter((option) => this.value.includes(option.value)).map((option) => option.label);
    }
    return [];
  }
  get displayedOptions(): { value: any; label: string }[] {
    const term = this.normalize(this.searchTerm);
    if (!this.searchable || !term) return this.options;
    return this.options.filter((o) => this.normalize(o.label).includes(term));
  }
  private normalize(s: string): string {
    return (s || '').toString().toLowerCase();
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.hasOpenedDropdown = true;
    }
  }

  onSearchChange(searchState: SearchState): void {
    this.searchText = searchState.value;
    if (!this.searchText) {
      this.filteredOptions = [...this.options];
      return;
    }
    this.filteredOptions = this.options.filter((option) =>
      option.label.toLowerCase().includes(this.searchText.toLowerCase()),
    );
  }

  get isShowError(): boolean {
    return this.isInvalid && (this.hasOpenedDropdown || !this.hasValue()) && !this.isOpen;
  }

  onAddItem(): void {
    const term = this.searchText.trim();
    if (!term) return;
    this.addItem.emit(term);
    this.searchText = '';
    this.filteredOptions = [...this.options];
    this.isOpen = false;
    this.hasOpenedDropdown = true;
  }

  onHeaderClick(event: MouseEvent) {
    if (this.disabled) return;

    const el = event.target as HTMLElement;
    if (el.closest('.dropdown-menu')) return;

    this.isOpen = !this.isOpen;
    if (this.isOpen && this.searchable) {
      queueMicrotask(() => this.searchInput?.nativeElement?.focus());
    }
  }
}
